import { Options, Vue } from 'vue-class-component';
import AxiosService from '@/views/calculators/services/axiosService';
import Autocomplete from 'vue3-autocomplete';
import { modalWithExit, togglePopup } from '@/views/calculators/services/AlertService';
import store from '@/store/store';
import { defaultConfig } from '@/cms/api/ApiConfig';
import AutocompleteComponent from '@/sharedcomponents/baseComponents/autoCompleteComponent/AutocompleteComponent';

type DarAddressResult = {
	type: string;
	id: string;
	titel: string;
};

type DarAddressResponse = {
	status: string;
	fund?: Array<DarAddressResult>;
};

const DAR_TYPE_ADRESSE = 'adresse';

@Options({
	name: 'AddressSearchComponent',
	components: {},
	props: {
		label: String,
		autocompleteId: String,
		defaultValue: { type: String, default: null },
		isValid: { type: Boolean, default: false },
		placeholder: String,
		readonlyValue: { type: String, default: null },
		visibleError: { type: Boolean, default: false },
	},
})
export default class AddressSearchComponent extends Vue {
	label: string;
	autocompleteId?: string;
	defaultValue?: string; // if model has value
	isValid?: boolean; // valid search result
	placeholder: string;
	readonlyValue?: string;
	visibleError: boolean;

	public dirty = true;
	private autocomplete: Autocomplete;
	public searching = false;
	public results: Array<DarAddressResult> = [];
	private doBlur = true;

	private searchUrl = '';
	public axiosService = new AxiosService();

	public mounted() {
		const calculatorProxyBase = `${defaultConfig.baseUrl}${store.state.openServiceCalculatorUrl}`;
		this.searchUrl = `${calculatorProxyBase}/adresser/soeg?tekst=`;
		this.axiosService.setWithCredentials(false);
	}

	get inputValid() {
		return !this.dirty && this.isValid && !this.searching && !this.readonlyValue;
	}
	get searchReady() {
		return !this.readonlyValue && !this.searching && (!this.isValid || this.dirty) && !this.showError;
	}

	get showError() {
		return this.visibleError && !this.readonlyValue && !this.searching && !this.isValid;
	}

	public setAutoCompleteField(input) {
		this.autocomplete = input;
		if (this.defaultValue) {
			this.autocomplete.setText(this.defaultValue);
			this.dirty = false;
		}
	}

	public displayItem(item: DarAddressResult) {
		return this.getDisplayText(item);
	}

	public async onFocus(): Promise<any> {
		this.dirty = true;
		await this.search(this.autocomplete.searchText);
	}

	public async search(input): Promise<any> {
		this.dirty = true;
		this.doBlur = true;

		this.results = [];
		if (input?.length < 2) {
			this.$emit('AddressSearch', {
				matchText: undefined,
			});
			this.searching = false;
			return;
		}
		this.searching = true;

		const results = await this.fetchAddressResults(input);
		if (results.length > 0) {
			this.results = results;
		} else {
			this.$emit('AddressSearch', {
				matchText: undefined,
			});
		}
		this.searching = false;
	}

	// Returns DAR soeg results
	private async fetchAddressResults(input: string): Promise<DarAddressResult[]> {
		const url = `${this.searchUrl}${encodeURIComponent(input)}`;
		const response = await this.axiosService.get(url);

		if (response?.status !== 200) {
			this.addressSearchErrorPopUp();
			return [];
		}

		return (response?.data as DarAddressResponse)?.fund ?? [];
	}

	public async handleSubmit(result: DarAddressResult) {
		const selectedText = this.getDisplayText(result);
		this.autocomplete.setText(selectedText);

		// For intermediate DAR types, treat selected text as fresh user input.
		if (result?.type !== DAR_TYPE_ADRESSE) {
			await this.search(selectedText);
			this.doBlur = false;
			(this.$refs.AbAutoComplete as AutocompleteComponent)?.forceSearch();
			return;
		}

		this.emit(result);
	}

	public blur(evt) {
		if (this.doBlur && this.results.length > 0) {
			if (this.autocomplete.searchText !== this.getDisplayText(this.results[0])) {
				this.handleSubmit(this.results[0]);
			}
		}
		this.doBlur = false;
	}

	private getDisplayText(result: DarAddressResult): string {
		if (!result) {
			return '';
		}

		return result.titel;
	}

	private emit(result: DarAddressResult) {
		if (result) {
			const res: any = {
				matchText: this.getDisplayText(result),
				id: result.id,
				data: result,
			};
			this.$emit('AddressSearch', res);
			this.searching = false;
			this.doBlur = false;
			this.dirty = false;
			this.autocomplete.autocompleteRef.blur();
		}
	}

	private addressSearchErrorPopUp() {
		const cms = { btnSecondLabel: 'Ok', btnSecondStyle: 'btn-outline-secondary' };
		const modal = modalWithExit(cms);
		modal.title = 'Problem med adressesøgning';
		modal.content =
			'Der er desværre i øjeblikket problemer med adressesøgning. Prøv igen om et øjeblik eller kontakt os.';
		modal.id = 'address search error';
		togglePopup(cms, modal);
	}
}
